import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { FlickeringBadge } from '~/components/elements/FlickeringBadge'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { compact, ensureExists, exists, findByPath, isAssigned } from '~/utils'

import { dialogInfoAtom, linkAnswersInfoAtom } from '../../atoms'
import { useMainQuerySurveys } from '../../hooks/useMainQuery'

import { type InputValues } from './utils'

export type LinkAnswersButtonProps = {
  index: number
}

export const LinkAnswersButton = ({ index }: LinkAnswersButtonProps) => {
  const { t } = useTranslation()
  const { control, getValues } = useFormContext<InputValues>()
  const attachedQuestionMap = useWatch({
    control,
    name: `questions.${index}.attachedQuestionMap`,
  })
  const options = useWatch({ control, name: `questions.${index}.options` })

  const isButtonShown = React.useMemo(
    () => R.values(attachedQuestionMap).every(isAssigned),
    [attachedQuestionMap],
  )

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveys = useMainQuerySurveys()
  const hasDifferences = React.useMemo(() => {
    if (!isButtonShown) {
      return false
    }

    const questions = R.pipe(
      dialogInfo?.surveyGroup?.surveys,
      ensureExists,
      R.map((surveyGroupSurvey) => {
        const attachedQuestionId = attachedQuestionMap[surveyGroupSurvey.id]
        if (typeof attachedQuestionId !== 'string') {
          return undefined
        }

        const survey = findByPath(surveys, 'id', surveyGroupSurvey.id)
        const question = findByPath(
          survey?.questions ?? [],
          'question_id',
          attachedQuestionId,
        )

        return question
      }),
      compact,
    )

    const answerOptions = questions.map(R.prop('answer_options'))
    const [firstAnswerOptions, ...restAnswerOptions] = answerOptions

    return !restAnswerOptions.every(R.isDeepEqual(firstAnswerOptions))
  }, [
    attachedQuestionMap,
    dialogInfo?.surveyGroup?.surveys,
    isButtonShown,
    surveys,
  ])

  const isNotResolvedDifferences = React.useMemo(
    () =>
      hasDifferences &&
      (options.length === 0 ||
        options.some(({ attachedOptionMap }) =>
          R.entries(attachedQuestionMap).some(
            ([surveyId, questionId]) =>
              exists(questionId) && !exists(attachedOptionMap[surveyId]),
          ),
        )),
    [attachedQuestionMap, hasDifferences, options],
  )

  const setLinkAnswersInfo = useSetAtom(linkAnswersInfoAtom)
  const handleClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    const question = getValues(`questions.${index}`)
    setLinkAnswersInfo({ index, question })
  }, [getValues, index, setLinkAnswersInfo])

  if (!isButtonShown) {
    return null
  }

  return (
    <StyledButton fillMode="outline" onClick={handleClick}>
      {isNotResolvedDifferences
        ? t('please_correct_inconsistencies_in_labels_1')
        : hasDifferences
          ? t('inconsistent_labels_resolved_1')
          : t('link_options')}

      {isNotResolvedDifferences && <FlickeringBadge color="error" />}
    </StyledButton>
  )
}
